import { useState } from 'react';
import AppShell from '../components/layout/AppShell';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { studentAPI } from '../utils/api';

const Section = ({ title, icon, children }) => (
  <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
    <div style={{ fontSize: '13px', fontWeight: 800, display: 'flex', alignItems: 'center', gap: '8px' }}>
      <span>{icon}</span>{title}
    </div>
    {children}
  </div>
);

const Field = ({ label, value, editable, type = 'text', onChange, placeholder, hint }) => (
  <div>
    <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--muted)', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.4px' }}>{label}</div>
    {editable ? (
      <input
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        style={{ width: '100%', padding: '9px 12px', border: '1.5px solid var(--border)', borderRadius: '8px', fontSize: '13px', fontWeight: 600, color: 'var(--text)', background: 'white', outline: 'none', boxSizing: 'border-box' }}
        onFocus={e => e.target.style.borderColor = 'var(--brand-primary)'}
        onBlur={e => e.target.style.borderColor = 'var(--border)'}
      />
    ) : (
      <div style={{ padding: '9px 12px', background: 'var(--bg)', borderRadius: '8px', fontSize: '13px', fontWeight: 600, color: 'var(--muted)', border: '1.5px solid var(--border)' }}>{value}</div>
    )}
    {hint && <div style={{ fontSize: '10px', color: 'var(--muted)', marginTop: '4px' }}>{hint}</div>}
  </div>
);

const SettingsPage = () => {
  const { user, setUser } = useAuth();
  const { success, error } = useToast();
  const { t } = useTranslation();

  const [name, setName] = useState(user?.name || '');
  const [phone, setPhone] = useState(user?.phone || '');
  const [savingProfile, setSavingProfile] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);

  const [newClass, setNewClass] = useState('');
  const [classReason, setClassReason] = useState('');
  const [savingClass, setSavingClass] = useState(false);
  const [classRequested, setClassRequested] = useState(false);

  const handleSaveProfile = async () => {
    if (!name.trim()) return error('Name cannot be empty');
    setSavingProfile(true);
    try {
      const res = await studentAPI.updateProfile({ name, phone });
      if (res.data.success) {
        setUser(u => ({ ...u, name, phone }));
        success('Profile updated successfully!');
      }
    } catch {
      error('Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) return error('Please fill all fields');
    if (newPassword.length < 8) return error('New password must be at least 8 characters');
    if (newPassword === currentPassword) return error('New password cannot be same as current password');
    if (newPassword !== confirmPassword) return error('Passwords do not match');
    setSavingPassword(true);
    try {
      const res = await studentAPI.changePassword({ currentPassword, newPassword });
      if (res.data.success) {
        success('Password changed successfully!');
        setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
      } else {
        error(res.data.message || 'Failed to change password');
      }
    } catch (err) {
      error(err.response?.data?.message || 'Failed to change password');
    } finally {
      setSavingPassword(false);
    }
  };

  const handleClassChangeRequest = async () => {
    if (!newClass) return error('Please select a class');
    if (parseInt(newClass) === user?.classId) return error('You are already in this class');
    setSavingClass(true);
    try {
      const res = await studentAPI.requestClassChange({ newClass: parseInt(newClass), reason: classReason });
      if (res.data.success) {
        setClassRequested(true);
        success('Class change request submitted! Admin will review it.');
      } else {
        error(res.data.message || 'Failed to submit request');
      }
    } catch {
      error('Failed to submit class change request');
    } finally {
      setSavingClass(false);
    }
  };

  return (
    <AppShell>
      <div className="topbar">
        <div style={{ fontSize: '15px', fontWeight: 800 }}>⚙️ Settings</div>
      </div>

      <div className="page-content" style={{ display: 'flex', flexDirection: 'column', gap: '16px', maxWidth: '680px' }}>

        {/* Profile info */}
        <Section title="My Profile" icon="👤">
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '12px', background: 'var(--bg)', borderRadius: '10px' }}>
            <div style={{ width: '52px', height: '52px', borderRadius: '50%', background: 'var(--brand-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px', fontWeight: 900, color: 'white', flexShrink: 0 }}>
              {user?.name?.charAt(0).toUpperCase()}
            </div>
            <div>
              <div style={{ fontSize: '15px', fontWeight: 800 }}>{user?.name}</div>
              <div style={{ fontSize: '11px', color: 'var(--muted)' }}>Class {user?.classId} · CBSE</div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <Field label="Full Name" value={name} editable onChange={setName} placeholder="Your full name" />
            <Field label="Phone Number" value={phone} editable onChange={setPhone} placeholder="+91 XXXXX XXXXX" />
            <Field label="Email Address" value={user?.email} editable={false} hint="Email cannot be changed" />
            <Field label="Current Class" value={`Class ${user?.classId}`} editable={false} hint="Use Class Change Request below" />
          </div>

          <button onClick={handleSaveProfile} disabled={savingProfile}
            style={{ background: 'var(--brand-primary)', color: 'white', border: 'none', borderRadius: '8px', padding: '10px 20px', fontSize: '13px', fontWeight: 800, cursor: 'pointer', alignSelf: 'flex-start' }}>
            {savingProfile ? 'Saving...' : '💾 Save Profile'}
          </button>
        </Section>

        {/* Change password */}
        <Section title="Change Password" icon="🔒">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <Field label="Current Password" value={currentPassword} editable type="password" onChange={setCurrentPassword} placeholder="Enter current password" />
            <Field label="New Password" value={newPassword} editable type="password" onChange={setNewPassword} placeholder="Min 8 characters" />
            <Field label="Confirm New Password" value={confirmPassword} editable type="password" onChange={setConfirmPassword} placeholder="Repeat new password" />
          </div>
          <button onClick={handleChangePassword} disabled={savingPassword}
            style={{ background: 'var(--brand-secondary)', color: 'white', border: 'none', borderRadius: '8px', padding: '10px 20px', fontSize: '13px', fontWeight: 800, cursor: 'pointer', alignSelf: 'flex-start' }}>
            {savingPassword ? 'Changing...' : '🔑 Change Password'}
          </button>
        </Section>

        {/* Class change request */}
        <Section title="Request Class Change" icon="📚">
          {classRequested ? (
            <div style={{ background: 'var(--green-light)', border: '1px solid var(--green)', borderRadius: '10px', padding: '16px', textAlign: 'center' }}>
              <div style={{ fontSize: '24px', marginBottom: '8px' }}>✅</div>
              <div style={{ fontSize: '13px', fontWeight: 800, color: 'var(--green)' }}>Request Submitted!</div>
              <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '4px' }}>Admin will review and approve your class change request.</div>
            </div>
          ) : (
            <>
              <div style={{ fontSize: '12px', color: 'var(--muted)', background: 'var(--yellow-light)', padding: '10px 12px', borderRadius: '8px' }}>
                ⚠️ Class change requires admin approval. Your previous progress will be archived, not deleted.
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--muted)', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.4px' }}>New Class</div>
                  <select value={newClass} onChange={e => setNewClass(e.target.value)}
                    style={{ width: '100%', padding: '9px 12px', border: '1.5px solid var(--border)', borderRadius: '8px', fontSize: '13px', fontWeight: 600, background: 'white', cursor: 'pointer' }}>
                    <option value="">Select class...</option>
                    {[6,7,8,9,10].filter(c => c !== user?.classId).map(c => (
                      <option key={c} value={c}>Class {c}</option>
                    ))}
                  </select>
                </div>
                <Field label="Reason (optional)" value={classReason} editable onChange={setClassReason} placeholder="e.g. Promoted to next class" />
              </div>
              <button onClick={handleClassChangeRequest} disabled={savingClass || !newClass}
                style={{ background: newClass ? 'var(--brand-primary)' : 'var(--border)', color: newClass ? 'white' : 'var(--muted)', border: 'none', borderRadius: '8px', padding: '10px 20px', fontSize: '13px', fontWeight: 800, cursor: newClass ? 'pointer' : 'not-allowed', alignSelf: 'flex-start' }}>
                {savingClass ? 'Submitting...' : '📤 Submit Request'}
              </button>
            </>
          )}
        </Section>

        {/* Account info */}
        <Section title="Account Info" icon="ℹ️">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            {[
              { label: 'Account Status', value: '✅ Active' },
              { label: 'Registration Type', value: user?.registrationPath === 'school' ? '🏫 School' : '👨‍👩‍👧 Individual' },
              { label: 'Platform', value: 'MyPersonalGuruji' },
              { label: 'Board', value: 'CBSE' },
            ].map(item => (
              <div key={item.label} style={{ background: 'var(--bg)', borderRadius: '8px', padding: '10px 12px' }}>
                <div style={{ fontSize: '10px', fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '3px' }}>{item.label}</div>
                <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text)' }}>{item.value}</div>
              </div>
            ))}
          </div>
        </Section>

      </div>
    </AppShell>
  );
};

export default SettingsPage;
